import { useCallback, useMemo, useState } from 'react';

import {
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';

import { FilterSelectIcon } from '@modules/_shared/components/feed-v2/components/ActivityFilters/FilterSelectIcon';
import {
  getFilterSelectStyles,
  menuItemStyles,
} from '@modules/_shared/components/feed-v2/components/ActivityFilters/filterSelectStyles';
import { useLocale } from '@shared/hooks/locale';

type ActivitySortSelectProps = {
  onSortOrderChange: (sortOrder: ActivitySortOrder) => void;
};

export enum ActivitySortOrder {
  PendingFirst = 'pending_first',
  CreatedDesc = 'created_desc',
  DoneDesc = 'done_desc',
}

export const ActivitySortSelect = ({
  onSortOrderChange,
}: ActivitySortSelectProps) => {
  const { t } = useLocale();
  const [sortOrder, setSortOrder] = useState<ActivitySortOrder>(
    ActivitySortOrder.PendingFirst,
  );

  const labels = useMemo(() => {
    return {
      [ActivitySortOrder.PendingFirst]: t('Pending first'),
      [ActivitySortOrder.CreatedDesc]: t('Recently created'),
      [ActivitySortOrder.DoneDesc]: t('Recently done'),
    };
  }, [t]);

  const menuItems = useMemo(
    () =>
      Object.keys(labels).map(key => (
        <MenuItem value={key} key={key} sx={menuItemStyles}>
          <Typography variant="body2">
            {labels[key as ActivitySortOrder]}
          </Typography>
        </MenuItem>
      )),
    [labels],
  );

  const handleSortOrderChange = useCallback(
    (e: SelectChangeEvent<ActivitySortOrder>) => {
      setSortOrder(e.target.value as ActivitySortOrder);
      onSortOrderChange(e.target.value as ActivitySortOrder);
    },
    [onSortOrderChange],
  );

  return (
    <FormControl
      size="small"
      sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
    >
      <Select
        value={sortOrder}
        onChange={handleSortOrderChange}
        IconComponent={FilterSelectIcon}
        input={<OutlinedInput />}
        renderValue={selected => labels[selected] ?? selected}
        MenuProps={{
          slotProps: {
            paper: { sx: { mt: 0.5 } },
          },
        }}
        sx={getFilterSelectStyles(sortOrder !== ActivitySortOrder.PendingFirst)}
      >
        {menuItems}
      </Select>
    </FormControl>
  );
};
